import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  AgentConversation, AgentConversationSummary, AgentProposal, CampaignLang, deleteAgentConversation, getAgentConversation,
  getAgentOverview, pushAgentProposal, sendAgentMessage, startAgentConversation
} from '../../../api/services/marketingService';
import { Avatar, LANGS, ago } from './shared';
import { LinkButton, StatusPill } from '../../../components/ui/dg';

/**
 * Agent de campagnes push (Communication › Agent, `?vue=agent`), expérimental.
 *
 * Une conversation avec l'IA, façon Codex : le fil au centre, la saisie en
 * bas, les conversations de l'équipe à gauche (`&conversation=<id>`).
 * L'agent lit les campagnes, les idées et le calendrier, puis pose des
 * propositions (P1, P2…) qu'on corrige en lui parlant. « Pousser en
 * relecture » crée la campagne en attente dans Campagnes ; une autre
 * personne l'approuve avant l'envoi. L'agent ne publie rien.
 *
 * La réponse se prépare en arrière-plan sur l'API : la page relit la
 * conversation toutes les 2,5 secondes tant que l'agent travaille.
 */

const POLL_MS = 2500;

const AUDIENCE: Record<AgentProposal['audience'], string> = { all: 'Tout le monde', renters: 'Locataires', owners: 'Propriétaires' };

const LINK_LABEL: Record<string, string> = {
  'dingo://map': 'Ouvre la carte',
  'dingo://owner/vehicles': 'Ouvre Mes véhicules',
  'dingo://owner/rentals': 'Ouvre Mes locations',
  'dingo://rentals': 'Ouvre Mes réservations',
  'dingo://help': 'Ouvre l’aide'
};

const STARTERS = [
  'Des idées de push pour ce week-end, côté locataires',
  'Qu’a-t-on envoyé ces dernières semaines, et qu’est-ce qui manque ?',
  'Un push pour que les propriétaires remettent leur véhicule en ligne avant les vacances'
];

/** Message lisible pour une erreur du fournisseur d'IA (même idée que dans les Idées). */
const aiErrorText = (message: string) => {
  if (/429|rate limit/i.test(message)) return 'L’IA est très sollicitée en ce moment, réessayez dans une minute.';
  if (/\b(500|502|503|504|529)\b|overloaded|high demand|unavailable/i.test(message)) return `Le service ${message.match(/\b(Mistral|Gemini|Claude)\b/)?.[1] || 'd’IA'} est saturé en ce moment, réessayez dans une minute.`;
  if (/401|403|unauthorized|tier/i.test(message)) return 'La clé d’IA est refusée par le fournisseur. Vérifiez-la dans Coolify.';
  if (/timeout|abort|à temps/i.test(message)) return `${message.match(/\b(Mistral|Gemini|Claude)\b/)?.[1] || 'L’IA'} a mis trop de temps à répondre, réessayez.`;
  // Réponse brute du fournisseur (JSON tronqué) : illisible pour l'équipe, le détail reste dans les journaux de l'API.
  if (/\b(Mistral|Gemini|Claude) \d{3}\b/.test(message)) return 'Le fournisseur d’IA a refusé la requête. Si ça recommence, le détail est dans les journaux de l’API.';
  return message;
};

/* ------------------------------------------------------------------ */
/* Texte de l'agent : paragraphes, listes « - », gras **…**             */
/* ------------------------------------------------------------------ */

const inline = (text: string) => text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
  /^\*\*[^*]+\*\*$/.test(part) ? <strong key={i} className="font-semibold text-[#0F172A]">{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
));

const AgentText = ({ text }: { text: string }) => {
  const blocks: React.ReactNode[] = [];
  let list: string[] = [];
  const flush = () => {
    if (list.length) {
      blocks.push(
        <ul key={`l${blocks.length}`} className="my-2 space-y-1 pl-5">
          {list.map((item, i) => <li key={i} className="list-disc marker:text-[#A5A3F0]">{inline(item)}</li>)}
        </ul>
      );
      list = [];
    }
  };
  text.split('\n').forEach((line) => {
    const item = line.match(/^\s*[-•*]\s+(.*)$/) || line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (item) { list.push(item[1]); return; }
    flush();
    if (line.trim()) blocks.push(<p key={`p${blocks.length}`} className="my-1.5">{inline(line)}</p>);
  });
  flush();
  return <div className="text-[14.5px] leading-[1.6] text-[#1E293B]">{blocks}</div>;
};

/* ------------------------------------------------------------------ */
/* Carte d'une proposition                                              */
/* ------------------------------------------------------------------ */

const ProposalCard = ({ proposal, busy, onPush, onOpen }: {
  proposal: AgentProposal; busy: boolean; onPush: () => void; onOpen: (ref: string) => void;
}) => {
  const written = LANGS.filter((l) => proposal.content[l.code]?.title);
  const [lang, setLang] = useState<CampaignLang>('fr');
  const shown = proposal.content[lang] || proposal.content.fr;
  const dir = LANGS.find((l) => l.code === lang)?.dir || 'ltr';
  const pushed = Boolean(proposal.id_campaign);
  const missing = LANGS.filter((l) => !proposal.content[l.code]?.title);

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-[#F1F2F8] px-4 py-2.5">
        <span className="font-mono text-[12.5px] font-bold text-[#4F46E5]">P{proposal.number}</span>
        <StatusPill tone="grey">{AUDIENCE[proposal.audience] || proposal.audience}</StatusPill>
        <span className="text-[12px] text-[#6B7280]">Push{proposal.link ? `, ${(LINK_LABEL[proposal.link] || proposal.link).toLowerCase()}` : ''}</span>
        <div className="ml-auto flex gap-0.5 rounded-lg bg-[#F1F2F8] p-0.5" role="tablist" aria-label="Langue">
          {written.map((l) => (
            <button key={l.code} type="button" role="tab" aria-selected={lang === l.code} onClick={() => setLang(l.code)}
              className={`rounded-md px-2 py-0.5 text-[11.5px] font-bold uppercase ${lang === l.code ? 'bg-white text-[#1E293B] shadow-sm' : 'text-[#6B7280] hover:text-[#1E293B]'}`}>
              {l.code}
            </button>
          ))}
        </div>
      </div>

      {/* Aperçu de la notification telle qu'elle arrive sur le téléphone. */}
      <div className="bg-[#F7F7FB] px-4 py-3.5">
        <div dir={dir} className="flex gap-3 rounded-[14px] bg-white px-3.5 py-3 shadow-[0_1px_3px_rgba(15,23,42,0.08)]">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#4F46E5] text-[13px] font-extrabold text-white">D</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[13.5px] font-semibold text-[#0F172A]">{shown?.title}</span>
              <span className="shrink-0 text-[11px] text-[#9CA3AF]">{dir === 'rtl' ? 'الآن' : lang === 'en' ? 'now' : 'maintenant'}</span>
            </div>
            <p className="mt-0.5 text-[13.5px] leading-[1.45] text-[#334155]">{shown?.body}</p>
          </div>
        </div>
        <div className="mt-1.5 flex justify-between px-1 text-[11px] text-[#9CA3AF]">
          <span>Titre {shown?.title.length || 0}/60</span>
          <span>Texte {shown?.body.length || 0}/240</span>
        </div>
      </div>

      {(proposal.note || missing.length > 0) && (
        <div className="space-y-1 px-4 pt-3 text-[12.5px] leading-[1.5] text-[#6B7280]">
          {proposal.note && <p>{proposal.note}</p>}
          {missing.length > 0 && <p className="text-[#B45309]">Pas encore rédigée en {missing.map((l) => l.label).join(', ')}. Ces comptes recevront le français.</p>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        {pushed ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#047857]">
              <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              {proposal.campaign_number ? `!${proposal.campaign_number}` : 'Campagne'} {proposal.campaign_status === 'sent' ? 'envoyée' : proposal.campaign_status === 'approved' ? 'approuvée' : proposal.campaign_status === 'rejected' ? 'refusée' : 'en relecture'}
            </span>
            {proposal.pushed_by_name && <span className="text-[12px] text-[#9CA3AF]">poussée par {proposal.pushed_by_name}</span>}
            {proposal.campaign_number && <LinkButton onClick={() => onOpen(String(proposal.campaign_number))}>Ouvrir la campagne</LinkButton>}
          </>
        ) : (
          <>
            <button type="button" disabled={busy} onClick={onPush}
              className="inline-flex h-9 items-center gap-2 rounded-2xl bg-[#4F46E5] px-4 text-[13px] font-bold text-white hover:bg-[#4338CA] disabled:opacity-50">
              {busy && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
              Pousser en relecture
            </button>
            <span className="text-[12px] leading-snug text-[#6B7280]">Elle arrive dans Campagnes. Une autre personne de l’équipe l’approuve avant l’envoi.</span>
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

const CampaignAgent = () => {
  const { token, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const conversationId = params.get('conversation');

  const [conversations, setConversations] = useState<AgentConversationSummary[]>([]);
  const [configured, setConfigured] = useState(true);
  const [model, setModel] = useState<string | null>(null);
  const [view, setView] = useState<AgentConversation | null>(null);
  const [loadingView, setLoadingView] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pushing, setPushing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  const setConversation = useCallback((id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set('conversation', id); else updated.delete('conversation');
    setParams(updated, { replace: true });
  }, [params, setParams]);

  const loadList = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getAgentOverview(token);
      setConversations(data.conversations);
      setConfigured(data.configured);
      setModel(data.model);
    } catch (err: any) {
      setError(err.message || 'Conversations indisponibles.');
    }
  }, [token]);

  useEffect(() => { loadList(); }, [loadList]);

  // Conversation ouverte : chargée à l'arrivée, puis relue tant que l'agent travaille.
  useEffect(() => {
    if (!token || !conversationId) { setView(null); return; }
    let cancelled = false;
    setLoadingView(true);
    getAgentConversation(token, conversationId)
      .then((data) => { if (!cancelled) setView(data); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Conversation introuvable.'); })
      .finally(() => { if (!cancelled) setLoadingView(false); });
    return () => { cancelled = true; };
  }, [token, conversationId]);

  const running = Boolean(view?.running);
  useEffect(() => {
    if (!token || !conversationId || !running) return;
    const timer = window.setInterval(async () => {
      try {
        const data = await getAgentConversation(token, conversationId);
        setView(data);
        if (!data.running) loadList();
      } catch { /* on réessaie au prochain passage */ }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [token, conversationId, running, loadList]);

  // Le fil descend en bas à chaque nouveau message ou proposition.
  const lastKey = `${view?.messages.length || 0}-${view?.proposals.map((p) => `${p.number}${p.message_id}${p.id_campaign}`).join('.')}-${running}`;
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastKey]);

  // La zone de saisie grandit avec le texte, jusqu'à une hauteur raisonnable.
  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [draft]);

  const send = async (text?: string) => {
    const content = (text ?? draft).trim();
    if (!token || !content || sending || running) return;
    setSending(true);
    setError(null);
    try {
      const data = conversationId
        ? await sendAgentMessage(token, conversationId, content)
        : await startAgentConversation(token, content);
      setView(data);
      setDraft('');
      if (!conversationId) setConversation(data.conversation.id_conversation);
      loadList();
    } catch (err: any) {
      setError(aiErrorText(err.message || 'Message non envoyé.'));
    } finally {
      setSending(false);
      input.current?.focus();
    }
  };

  const push = async (proposal: AgentProposal) => {
    if (!token) return;
    setPushing(proposal.id_proposal);
    setError(null);
    try {
      const { proposal: updated, campaign } = await pushAgentProposal(token, proposal.id_proposal);
      setView((v) => (v ? {
        ...v,
        proposals: v.proposals.map((p) => (p.id_proposal === updated.id_proposal
          ? { ...p, ...updated, campaign_number: campaign?.number ?? p.campaign_number, campaign_status: campaign?.status ?? p.campaign_status, pushed_by_name: p.pushed_by_name || [(user as any)?.first_name, (user as any)?.last_name].filter(Boolean).join(' ') || null }
          : p))
      } : v));
      setNotice(campaign?.number ? `!${campaign.number} est en relecture dans Campagnes.` : 'La campagne est en relecture dans Campagnes.');
      loadList();
    } catch (err: any) {
      setError(err.message || 'La proposition n’a pas pu être poussée.');
    } finally {
      setPushing(null);
    }
  };

  const remove = async (id: string) => {
    if (!token || !window.confirm('Supprimer cette conversation ? Les campagnes déjà poussées restent dans Campagnes.')) return;
    try {
      await deleteAgentConversation(token, id);
      if (id === conversationId) setConversation(null);
      loadList();
    } catch (err: any) {
      setError(err.message || 'Suppression impossible.');
    }
  };

  const openCampaign = (ref: string) => {
    const updated = new URLSearchParams(params);
    updated.delete('vue');
    updated.delete('conversation');
    updated.set('campagne', ref);
    setParams(updated);
  };

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  // Propositions rangées sous la réponse qui les a posées ; les autres en fin de fil.
  const byMessage = new Map<string, AgentProposal[]>();
  const orphans: AgentProposal[] = [];
  const messageIds = new Set((view?.messages || []).map((m) => String(m.id_message)));
  (view?.proposals || []).forEach((p) => {
    const key = p.message_id ? String(p.message_id) : '';
    if (key && messageIds.has(key)) byMessage.set(key, [...(byMessage.get(key) || []), p]);
    else orphans.push(p);
  });

  const cards = (list: AgentProposal[]) => list.map((p) => (
    <ProposalCard key={p.id_proposal} proposal={p} busy={pushing === p.id_proposal} onPush={() => push(p)} onOpen={openCampaign} />
  ));

  const empty = !conversationId;
  const locked = sending || running || !configured;

  return (
    <div className="flex min-h-0 flex-1">
      {/* Conversations de l'équipe */}
      <aside className="hidden w-[264px] shrink-0 flex-col border-r border-[#ECEDF3] bg-white lg:flex">
        <div className="px-3 pb-2 pt-3">
          <button type="button" onClick={() => { setConversation(null); setDraft(''); setTimeout(() => input.current?.focus(), 0); }}
            className="flex h-10 w-full items-center gap-2 rounded-xl border border-[#E5E7EB] px-3 text-[13px] font-semibold text-[#1E293B] hover:bg-[#F7F7FB]">
            <svg viewBox="0 0 16 16" className="h-4 w-4 text-[#6B7280]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M8 3v10M3 8h10" /></svg>
            Nouvelle conversation
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {conversations.length === 0 && <p className="px-2 py-3 text-[12.5px] text-[#9CA3AF]">Aucune conversation pour l’instant.</p>}
          {conversations.map((c) => (
            <div key={c.id_conversation}
              className={`group flex items-start gap-1 rounded-xl px-2.5 py-2 ${c.id_conversation === conversationId ? 'bg-[#EEEDFD]' : 'hover:bg-[#F7F7FB]'}`}>
              <button type="button" onClick={() => setConversation(c.id_conversation)} className="min-w-0 flex-1 text-left">
                <span className="block truncate text-[13px] font-semibold text-[#1E293B]">{c.title || 'Sans titre'}</span>
                <span className="mt-0.5 block truncate text-[11.5px] text-[#6B7280]">
                  {c.running ? 'L’agent répond' : ago(c.updated_at)}
                  {c.created_by_name ? `, ${c.created_by_name.split(' ')[0]}` : ''}
                  {c.pushed_count > 0 ? `, ${c.pushed_count} poussée${c.pushed_count > 1 ? 's' : ''}` : ''}
                </span>
              </button>
              <button type="button" onClick={() => remove(c.id_conversation)} aria-label="Supprimer la conversation"
                className="mt-0.5 rounded-md p-1 text-[#9CA3AF] opacity-0 hover:bg-white hover:text-[#B91C1C] focus:opacity-100 group-hover:opacity-100">
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 4l8 8M12 4l-8 8" /></svg>
              </button>
            </div>
          ))}
        </div>
        <p className="border-t border-[#F1F2F8] px-4 py-3 text-[11.5px] leading-[1.5] text-[#9CA3AF]">
          Partagées avec toute l’équipe. {model ? `Modèle ${model}.` : ''}
        </p>
      </aside>

      {/* Fil */}
      <section className="flex min-w-0 flex-1 flex-col bg-[#F7F7FB]">
        <div className="flex items-center gap-2 border-b border-[#ECEDF3] bg-white px-4 py-2.5 lg:px-6">
          <span className="truncate text-[14px] font-bold text-[#1E293B]">{view?.conversation.title || 'Agent de campagnes push'}</span>
          <span className="rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[11px] font-bold text-[#92400E]">Expérimental</span>
          {!empty && (
            <button type="button" onClick={() => setConversation(null)} className="ml-auto text-[12.5px] font-semibold text-[#4F46E5] lg:hidden">Nouvelle</button>
          )}
        </div>

        <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[760px] px-4 pb-6 pt-6 lg:px-6">
            {!configured && (
              <div className="mb-4 rounded-xl bg-[#FDECEF] px-4 py-3 text-[13px] text-[#B91C1C]">
                Aucune clé d’IA n’est configurée sur l’API. Ajoutez MISTRAL_API_KEY, GEMINI_API_KEY ou ANTHROPIC_API_KEY dans Coolify.
              </div>
            )}

            {empty ? (
              <div className="pt-[8vh]">
                <h2 className="text-[22px] font-extrabold tracking-[-0.2px] text-[#1E293B]">Quelle campagne push préparer ?</h2>
                <p className="mt-2 max-w-[560px] text-[14px] leading-[1.6] text-[#475569]">
                  L’agent connaît les campagnes déjà envoyées ou en attente, les idées du calendrier et l’activité de l’app.
                  Il propose des textes en français, anglais et arabe, que vous ajustez en lui répondant.
                  Rien ne part sans passer par Campagnes et l’approbation d’une autre personne.
                </p>
                <div className="mt-6 grid gap-2">
                  {STARTERS.map((s) => (
                    <button key={s} type="button" disabled={locked} onClick={() => send(s)}
                      className="rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-left text-[13.5px] text-[#334155] hover:border-[#C7C4F6] hover:text-[#1E293B] disabled:opacity-50">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : loadingView && !view ? (
              <p className="py-10 text-center text-[13px] text-[#9CA3AF]">Chargement de la conversation</p>
            ) : (
              <ol className="space-y-6">
                {(view?.messages || []).map((m) => (
                  <li key={m.id_message}>
                    {m.role === 'user' ? (
                      <div className="flex justify-end gap-2.5">
                        <div className="max-w-[85%]">
                          <div className="mb-1 text-right text-[11.5px] text-[#9CA3AF]">{m.author_name || 'Équipe'}, {ago(m.created_at)}</div>
                          <div className="whitespace-pre-wrap rounded-2xl rounded-tr-md bg-white px-4 py-2.5 text-[14.5px] leading-[1.55] text-[#1E293B] shadow-[0_1px_2px_rgba(15,23,42,0.06)]">{m.content}</div>
                        </div>
                        <Avatar name={m.author_name} photo={m.author_photo} size={28} />
                      </div>
                    ) : (
                      <div className="flex gap-2.5">
                        <Avatar ai size={28} title="Agent" />
                        <div className="min-w-0 flex-1">
                          <div className="mb-0.5 text-[11.5px] text-[#9CA3AF]">Agent{m.model ? `, ${m.model}` : ''}</div>
                          <AgentText text={m.content} />
                          {cards(byMessage.get(String(m.id_message)) || [])}
                        </div>
                      </div>
                    )}
                  </li>
                ))}
                {orphans.length > 0 && <li className="pl-[38px]">{cards(orphans)}</li>}
                {running && (
                  <li className="flex items-center gap-2.5">
                    <Avatar ai size={28} title="Agent" />
                    <span className="inline-flex items-center gap-2 text-[13.5px] text-[#6B7280]">
                      <span className="flex gap-1">
                        {[0, 1, 2].map((i) => <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#A5A3F0]" style={{ animationDelay: `${i * 150}ms` }} />)}
                      </span>
                      L’agent consulte les campagnes et prépare sa réponse
                    </span>
                  </li>
                )}
                {view?.error && !running && (
                  <li className="ml-[38px] rounded-xl bg-[#FDECEF] px-4 py-3 text-[13px] text-[#B91C1C]">
                    L’agent n’a pas pu répondre. {aiErrorText(view.error)} Renvoyez votre message pour réessayer.
                    {/* Expérimental : la réponse brute du fournisseur, à transmettre si l'erreur revient. */}
                    {aiErrorText(view.error) !== view.error && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[12px] font-semibold text-[#B91C1C]/80">Voir le détail</summary>
                        <pre className="mt-1.5 whitespace-pre-wrap break-all rounded-lg bg-white/70 px-2.5 py-2 font-mono text-[11.5px] leading-[1.5] text-[#7F1D1D]">{view.error}</pre>
                      </details>
                    )}
                  </li>
                )}
              </ol>
            )}
          </div>
        </div>

        {/* Saisie */}
        <div className="border-t border-[#ECEDF3] bg-[#F7F7FB] px-4 pb-4 pt-3 lg:px-6">
          <div className="mx-auto w-full max-w-[760px]">
            {error && <p className="mb-2 rounded-lg bg-[#FDECEF] px-3 py-2 text-[12.5px] text-[#B91C1C]">{error}</p>}
            {notice && <p className="mb-2 rounded-lg bg-[#ECFDF5] px-3 py-2 text-[12.5px] font-semibold text-[#047857]">{notice}</p>}
            <form onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-end gap-2 rounded-2xl border border-[#E5E7EB] bg-white p-2 pl-4 focus-within:border-[#C7C4F6] focus-within:ring-4 focus-within:ring-[#4F46E5]/10">
              <textarea ref={input} rows={1} value={draft} disabled={!configured}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
                placeholder={running ? 'L’agent répond, vous pourrez écrire juste après' : empty ? 'Décrivez la campagne, l’occasion ou le public visé' : 'Demandez un changement, une autre idée, une autre audience'}
                className="max-h-[200px] min-h-[24px] flex-1 resize-none bg-transparent py-1.5 text-[14.5px] leading-[1.55] text-[#1E293B] placeholder-[#9CA3AF] focus:outline-none" />
              <button type="submit" disabled={locked || !draft.trim()} aria-label="Envoyer"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#4F46E5] text-white hover:bg-[#4338CA] disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF]">
                {sending
                  ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  : <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" /></svg>}
              </button>
            </form>
            <p className="mt-1.5 px-1 text-[11.5px] text-[#9CA3AF]">Entrée pour envoyer, Maj et Entrée pour aller à la ligne. L’agent propose, il ne publie jamais.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CampaignAgent;
